import { useState, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { FaEllipsisV, FaCheck, FaTimes, FaLock, FaLockOpen, FaArrowsAlt, FaTrash, FaEdit, FaInfoCircle, FaExpand } from 'react-icons/fa';

// Context to provide fullscreen state to children
export const BaseComponentFullscreenContext = createContext(false);

export function useBaseComponentFullscreen() {
  return useContext(BaseComponentFullscreenContext);
}

export default function BaseComponent({
  component,
  onUpdate,
  onDelete,
  isOwner,
  icon,
  children,
  disclaimer,
  onReorder
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState(component.title);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  // Fullscreen title editing state (must be at top level)
  const [fullscreenEditingTitle, setFullscreenEditingTitle] = useState(false);
  const [fullscreenTitle, setFullscreenTitle] = useState(component.title);

  const handleUpdateTitle = () => {
    if (newTitle.trim()) {
      onUpdate({
        ...component,
        title: newTitle.trim()
      });
      setEditingTitle(false);
    }
  };

  const togglePublic = () => {
    onUpdate({
      ...component,
      is_public: !component.is_public
    });
    setShowMenu(false);
  };

  const confirmDelete = () => {
    setShowDeleteConfirm(true);
    setShowMenu(false);
  };

  const handleDelete = () => {
    onDelete(component.id);
    setShowDeleteConfirm(false);
  };

  return (
    <BaseComponentFullscreenContext.Provider value={fullscreen}>
    <div className="component-container bg-[var(--bg-secondary)] rounded-lg shadow overflow-hidden">
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-[var(--bg-primary)] p-6 rounded-lg max-w-sm w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">Delete Component</h3>
            <p className="mb-6">Are you sure you want to delete "{component.title}"? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-lg bg-[var(--secondary)] bg-opacity-20 text-[var(--secondary)] border border-[var(--secondary)] border-opacity-30 hover:bg-[var(--secondary)] hover:bg-opacity-30 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-danger hover:bg-danger hover:scale-105 hover:shadow-lg text-white transition-all duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Header */}
      <div className="component-header flex items-center justify-between p-4 border-b border-[var(--border-color)]">
        <div className="flex items-center gap-3">
          <div className="text-xl text-[var(--primary)]">
            {icon}
          </div>
          {editingTitle ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="px-2 py-1 w-full border border-[var(--border-color)] rounded focus:outline-none focus:ring-1 focus:ring-[var(--primary)] bg-[var(--bg-primary)] text-[var(--text-primary)]"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onBlur={handleUpdateTitle}
                onKeyPress={(e) => e.key === 'Enter' && handleUpdateTitle()}
                autoFocus
              />
              <button 
                onClick={handleUpdateTitle}
                className="p-1 text-[var(--primary)] hover:text-[var(--primary)] hover:opacity-80 transition-opacity"
              >
                <FaCheck />
              </button>
              <button 
                onClick={() => {
                  setEditingTitle(false);
                  setNewTitle(component.title);
                }}
                className="p-1 text-[var(--secondary)] hover:text-danger transition-colors"
              >
                <FaTimes />
              </button>
            </div>
          ) : (
            <h3 className="font-medium text-[var(--text-primary)]">
              {component.title}
            </h3>
          )}
        </div>

        {isOwner && (
          <div className="relative flex items-center gap-2">
            <button
              onClick={() => setFullscreen(!fullscreen)}
              className="hidden lg:block p-1 text-[var(--secondary)] hover:text-[var(--primary)] transition-colors"
              title="Toggle Fullscreen"
            >
              <FaExpand />
            </button>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 text-[var(--secondary)] hover:text-[var(--primary)] transition-colors"
            >
              <FaEllipsisV />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-[var(--bg-primary)] rounded-md shadow-lg z-10 border border-[var(--border-color)]">
                <div className="py-1">
                  <button
                    onClick={togglePublic}
                    className="flex items-center px-4 py-2 text-sm w-full text-left hover:bg-[var(--bg-hover)]"
                  >
                    {component.is_public ? (
                      <><FaLockOpen className="mr-2" /> Make Private</>
                    ) : (
                      <><FaLock className="mr-2" /> Make Public</>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      if (onReorder) {
                        onReorder();
                      }
                    }}
                    className="flex items-center px-4 py-2 text-sm w-full text-left hover:bg-[var(--bg-hover)]"
                  >
                    <FaArrowsAlt className="mr-2" /> Reorder
                  </button>
                  <button
                    onClick={() => {
                      setEditingTitle(true);
                      setShowMenu(false);
                    }}
                    className="flex items-center px-4 py-2 text-sm w-full text-left hover:bg-[var(--bg-hover)]"
                  >
                    <FaEdit className="mr-2" /> Edit Title
                  </button>
                  <button
                    onClick={() => {
                      setFullscreen(true);
                      setShowMenu(false);
                    }}
                    className="hidden lg:flex items-center px-4 py-2 text-sm w-full text-left hover:bg-[var(--bg-hover)]"
                  >
                    <FaExpand className="mr-2" /> Fullscreen
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="flex items-center px-4 py-2 text-sm text-danger w-full text-left hover:bg-[var(--bg-hover)]"
                  >
                    <FaTrash className="mr-2" /> Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="component-content p-4">
      {children}
      </div>

      {/* Footer */}
      <div className="component-footer px-4 py-2 text-xs text-[var(--text-secondary)] border-t border-[var(--border-color)] flex items-center justify-between">
        <div>
          <span>Last edited: {new Date(component.last_edited_at).toLocaleString()}</span>
        </div>
        {disclaimer && (
          <div className="group relative">
            <FaInfoCircle className="text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors" />
            <span className="absolute hidden group-hover:block bg-[var(--bg-primary)] text-[var(--text-primary)] text-xs p-2 rounded shadow-lg bottom-6 right-0 w-48 z-10 border border-[var(--border-color)]">
              {disclaimer}
            </span>
          </div>
        )}
      </div>
      {/* Fullscreen Modal */}
      {fullscreen && createPortal(
        <div className="fixed inset-0 z-[10000] bg-black bg-opacity-60 flex items-center justify-center animate-fade-in">
          <div className="relative bg-[var(--bg-secondary)] rounded-lg shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-auto animate-scale-in border border-[var(--border-color)]">
            {/* Title Bar with distinct color */}
            <div className="flex items-center justify-between px-6 py-3 bg-[var(--primary)] text-white border-b border-[var(--border-color)]">
              <div className="flex items-center gap-3">
                <div className="text-xl">{icon}</div>
                {fullscreenEditingTitle ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      className="px-2 py-1 w-48 border border-[var(--border-color)] rounded focus:outline-none focus:ring-1 focus:ring-white bg-white text-black"
                      value={fullscreenTitle}
                      onChange={(e) => setFullscreenTitle(e.target.value)}
                      onBlur={() => {
                        if (fullscreenTitle.trim()) {
                          onUpdate({ ...component, title: fullscreenTitle.trim() });
                        }
                        setFullscreenEditingTitle(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (fullscreenTitle.trim()) {
                            onUpdate({ ...component, title: fullscreenTitle.trim() });
                          }
                          setFullscreenEditingTitle(false);
                        }
                        if (e.key === 'Escape') {
                          setFullscreenEditingTitle(false);
                          setFullscreenTitle(component.title);
                        }
                      }}
                      autoFocus
                    />
                    <button 
                      onClick={() => {
                        if (fullscreenTitle.trim()) {
                          onUpdate({ ...component, title: fullscreenTitle.trim() });
                        }
                        setFullscreenEditingTitle(false);
                      }}
                      className="p-1 text-white hover:opacity-80 transition-opacity"
                    >
                      <FaCheck />
                    </button>
                    <button 
                      onClick={() => {
                        setFullscreenEditingTitle(false);
                        setFullscreenTitle(component.title);
                      }}
                      className="p-1 text-white hover:text-danger transition-colors"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <h3 className="font-bold text-white text-xl flex items-center gap-2">
                    {component.title}
                    <button
                      onClick={() => setFullscreenEditingTitle(true)}
                      className="ml-2 p-1 text-white/80 hover:text-white"
                      title="Edit Title"
                    >
                      <FaEdit />
                    </button>
                  </h3>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePublic}
                  className="flex items-center px-3 py-1 text-sm rounded bg-white/10 hover:bg-white/20 transition-colors"
                >
                  {component.is_public ? (
                    <><FaLockOpen className="mr-2" /> Make Private</>
                  ) : (
                    <><FaLock className="mr-2" /> Make Public</>
                  )}
                </button>
                <button
                  className="text-2xl text-white/80 hover:text-white ml-2"
                  onClick={() => setFullscreen(false)}
                  title="Close Fullscreen"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            {/* Content */}
            <div className="component-content flex-1 overflow-auto px-6 py-5">
              {children}
            </div>
            {/* Footer */}
            <div className="component-footer px-6 py-3 text-xs text-[var(--text-secondary)] border-t border-[var(--border-color)] flex items-center justify-between bg-[var(--bg-secondary)]">
              <div>
                <span>Last edited: {new Date(component.last_edited_at).toLocaleString()}</span>
              </div>
              {disclaimer && (
                <div className="group relative">
                  <FaInfoCircle className="text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors" />
                  <span className="absolute hidden group-hover:block bg-[var(--bg-primary)] text-[var(--text-primary)] text-xs p-2 rounded shadow-lg bottom-6 right-0 w-48 z-10 border border-[var(--border-color)]">
                    {disclaimer}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
    </BaseComponentFullscreenContext.Provider>
  );
}

// Add animation CSS (if not already present in your global styles):
// .animate-fade-in { animation: fadeIn 0.3s; }
// .animate-scale-in { animation: scaleIn 0.3s cubic-bezier(0.4,0,0.2,1); }
// @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
// @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }